import { FirestoreQuery } from '@shared/types';
import { SavedQuery } from '../types';

const STORAGE_KEY = 'firestore-saved-queries';
const DEFAULT_QUERY_STORAGE_KEY = 'firestore-default-query';

export interface StoredDefaultQueryByConnection {
  [connectionId: string]: { [collectionId: string]: string };
}

export interface StoredSavedQueriesByConnection {
  [connectionId: string]: { [collectionId: string]: SavedQuery[] };
}

function loadAll(): StoredSavedQueriesByConnection {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    if (Array.isArray(parsed)) return {};
    return (parsed as StoredSavedQueriesByConnection) ?? {};
  } catch (error) {
    console.error('Error loading saved queries:', error);
    return {};
  }
}

function saveAll(data: StoredSavedQueriesByConnection): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving saved queries:', error);
  }
}

function loadDefaultQueryAll(): StoredDefaultQueryByConnection {
  try {
    const stored = localStorage.getItem(DEFAULT_QUERY_STORAGE_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    return (parsed as StoredDefaultQueryByConnection) ?? {};
  } catch (error) {
    console.error('Error loading default query:', error);
    return {};
  }
}

function saveDefaultQueryAll(data: StoredDefaultQueryByConnection): void {
  try {
    localStorage.setItem(DEFAULT_QUERY_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving default query:', error);
  }
}

export function getDefaultQueryName(
  connectionId: string,
  collectionId: string
): string | null {
  if (!connectionId || !collectionId) return null;
  const data = loadDefaultQueryAll();
  const name = data[connectionId]?.[collectionId];
  return name ?? null;
}

export function setDefaultQuery(
  connectionId: string,
  collectionId: string,
  queryName: string
): void {
  if (!connectionId || !collectionId) return;
  const data = loadDefaultQueryAll();
  if (!data[connectionId]) data[connectionId] = {};
  data[connectionId][collectionId] = queryName;
  saveDefaultQueryAll(data);
}

export function clearDefaultQuery(connectionId: string, collectionId: string): void {
  if (!connectionId || !collectionId) return;
  const data = loadDefaultQueryAll();
  if (!data[connectionId]?.[collectionId]) return;
  delete data[connectionId][collectionId];
  if (Object.keys(data[connectionId]).length === 0) delete data[connectionId];
  saveDefaultQueryAll(data);
}

export function loadQueries(connectionId: string, collectionId: string): SavedQuery[] {
  if (!connectionId || !collectionId) return [];
  const data = loadAll();
  return data[connectionId]?.[collectionId] ?? [];
}

export function saveQuery(
  connectionId: string,
  collectionId: string,
  name: string,
  queryConfig: FirestoreQuery
): void {
  if (!connectionId || !collectionId) return;
  const trimmedName = name.trim();
  const data = loadAll();
  if (!data[connectionId]) data[connectionId] = {};
  const list = data[connectionId][collectionId] ?? [];
  const existingIndex = list.findIndex((q) => q.name.trim() === trimmedName);
  const queryToSave: SavedQuery = {
    name: trimmedName,
    query: queryConfig,
    createdAt: new Date().toISOString(),
  };
  if (existingIndex >= 0) {
    list[existingIndex] = queryToSave;
  } else {
    list.push(queryToSave);
  }
  data[connectionId][collectionId] = list;
  saveAll(data);
}

export function deleteQuery(
  connectionId: string,
  collectionId: string,
  name: string
): void {
  if (!connectionId || !collectionId) return;
  if (getDefaultQueryName(connectionId, collectionId) === name) {
    clearDefaultQuery(connectionId, collectionId);
  }
  const data = loadAll();
  const list = data[connectionId]?.[collectionId] ?? [];
  const filtered = list.filter((q) => q.name !== name);
  if (filtered.length === 0) {
    if (data[connectionId]) {
      delete data[connectionId][collectionId];
      if (Object.keys(data[connectionId]).length === 0) delete data[connectionId];
    }
  } else {
    data[connectionId][collectionId] = filtered;
  }
  saveAll(data);
}

export function loadQuery(
  connectionId: string,
  collectionId: string,
  name: string
): FirestoreQuery | null {
  const saved = loadQueries(connectionId, collectionId);
  const found = saved.find((q) => q.name === name);
  return found ? found.query : null;
}

/**
 * Clears all saved queries and default-query data from localStorage.
 * Used on Sign Out & Forget so no query preferences persist after re-auth.
 */
export function clearSavedQueriesStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(DEFAULT_QUERY_STORAGE_KEY);
  } catch (e) {
    console.error('Failed to clear saved queries storage', e);
  }
}
